import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../ui/Button';

const Hero = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    if (isHome) {
      const element = document.getElementById(targetId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    } else {
      navigate('/', { state: { scrollTo: targetId } });
    }
  };

  return (
    <section className="relative min-h-[100svh] flex items-center pt-24 overflow-hidden border-b border-brand-text/10">
      {/* Architectural Grid Background */}
      <div className="absolute inset-0 grid grid-cols-4 md:grid-cols-12 gap-0 pointer-events-none opacity-20">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-r border-brand-text/20 h-full hidden md:block"></div>
        ))}
        {[...Array(4)].map((_, i) => (
          <div key={`m-${i}`} className="border-r border-brand-text/20 h-full md:hidden"></div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="lg:col-span-7 pt-12 md:pt-0"
        >
          <div className="font-mono text-xs uppercase tracking-widest mb-8 flex items-center gap-4 text-brand-text/70">
            <span className="w-12 h-px bg-brand-text/50"></span>
            Die serielle Modulbauweise
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.85] mb-8 text-balance">
            SCHNELLER BAUEN.<br />
            <span className="text-brand-text/50">MIT SYSTEM.</span>
          </h1>
          <p className="text-lg md:text-xl font-inter mb-12 max-w-lg text-brand-text/80 leading-relaxed">
            51north bricht mit den Ineffizienzen des klassischen Baubetriebs. Absolute Festpreisgarantie, kompromisslose Bauqualität und ein sorgenfreier Einzug in Ihr neues Zuhause.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button onClick={(e) => handleNavClick(e, 'contact')} showArrow>
              Projekt anfragen
            </Button>
            <button 
              onClick={(e) => handleNavClick(e, 'method')}
              className="inline-flex items-center justify-center px-6 py-3 border border-brand-text/30 font-inter font-medium text-sm hover:border-brand-text transition-colors cursor-pointer"
            >
              Unsere Methode
            </button>
          </div>
        </motion.div>

        {/* Modular Composition */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
          className="lg:col-span-5 relative h-[50vh] lg:h-[70vh] w-full mt-12 lg:mt-0"
        >
          <div className="absolute inset-0 grid grid-cols-2 grid-rows-3 gap-4">
            {/* Image 1: 70% Zeitersparnis */}
            <div className="row-span-2 relative overflow-hidden bg-brand-text/5 border border-brand-text/10 group">
              <img 
                src="/images/quartier-zeitersparnis.webp"
                alt="Wohnquartier – Rendering"
                className="absolute inset-0 w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ease-out" 
              />
              <div className="absolute bottom-4 left-4 font-mono text-[10px] uppercase bg-brand-bg px-2 py-1 z-10 border border-brand-text/10">Bis zu 70% Zeitersparnis</div>
            </div>

            <div className="relative border border-brand-text/10 flex flex-col justify-between p-4 bg-brand-bg">
              <div className="w-2 h-2 bg-brand-text"></div>
              <div className="font-mono text-xs">90%<br/>Hallenfertigung</div>
            </div>

            {/* Image 2: Под 90% Hallenfertigung */}
            <div className="relative overflow-hidden bg-brand-text/5 border border-brand-text/10 group">
              <img 
                src="/images/quartier-green-living.webp"
                alt="Quartier Green Living"
                className="absolute inset-0 w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ease-out" 
              />
            </div>

            <div className="col-span-2 relative border border-brand-text/10 flex items-center justify-center bg-brand-text/5">
              <span className="font-mono text-[10px] uppercase tracking-widest">+10.000m² Realisierte Fläche</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;